import { useEffect, useRef, useState, useCallback } from "react";
import { getFuturesContractSize } from "../shared/getContractSize";

/**
 * Bitget USDT-M futures (public WS v2)
 * WS: wss://ws.bitget.com/v2/ws/public
 * - ticker: {instType:"USDT-FUTURES", channel:"ticker", instId:"ETHUSDT"}
 * - orderbook: {instType:"USDT-FUTURES", channel:"books",  instId:"ETHUSDT"}
 * books: first is snapshot, then updates
 * fundingRate inside ticker payload (fundingRate)
 */
export const useBitgetFutures = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError]             = useState(null);
  const [tickerData, setTickerData]   = useState(null);
  const [orderBook, setOrderBook]     = useState({ asks: [], bids: [] });
  const [fundingRate, setFundingRate] = useState(0);

  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const animationFrameRef = useRef(null);

  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasSnapshotRef = useRef(false);
  const lastSeqRef = useRef(null);
  const contractSizeRef = useRef(1);

  const log = (...a) => console.log("🟪 [BITGET FUTURES]", ...a);

  const scaleBook = useCallback((ob) => {
    const k = contractSizeRef.current || 1;
    const mul = (arr) => arr.map(([p,q]) => [p, String(Number(q) * k)]);
    return { asks: mul(ob.asks || []), bids: mul(ob.bids || []) };
  }, []);

  const toPairs = (levels) =>
    Array.isArray(levels)
      ? levels.map(([p, q]) => [String(p), String(q)]).filter(([, q]) => +q >= 0)
      : [];

  const applyDeltas = useCallback((levels, side) => {
    const base = [...orderBookRef.current[side]];

    levels.forEach(([price, qty]) => {
      const pn = +price, qn = +qty;
      const idx = base.findIndex(([p]) => +p === pn);

      if (qn === 0) {
        if (idx !== -1) base.splice(idx, 1);
      } else {
        if (idx !== -1) base[idx] = [price, qty];
        else base.push([price, qty]);
      }
    });

    orderBookRef.current[side] =
      side === "asks"
        ? base.sort((a,b)=>+a[0]-+b[0]).slice(0,20)
        : base.sort((a,b)=>+b[0]-+a[0]).slice(0,20);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook(scaleBook(orderBookRef.current)); // 👈 в монеты
    });
  }, [scaleBook]);

  const subscribeAll = useCallback((ws, instId) => {
    const args = [
      { instType: "USDT-FUTURES", channel: "ticker", instId },
      { instType: "USDT-FUTURES", channel: "books",  instId },
    ];
    ws.send(JSON.stringify({ op: "subscribe", args }));
    log("→ SUB", args);
  }, []);

  const unsubscribeAll = useCallback((ws, instId) => {
    try {
      const args = [
        { instType: "USDT-FUTURES", channel: "ticker", instId },
        { instType: "USDT-FUTURES", channel: "books",  instId },
      ];
      ws?.send(JSON.stringify({ op: "unsubscribe", args }));
      log("→ UNSUB", args);
    } catch {}
  }, []);

  const handleMessage = useCallback((event) => {
    try {
      if (typeof event.data !== "string") return;
      if (event.data === "pong") return;

      const msg = JSON.parse(event.data);
      if (msg?.event) { log("evt:", msg.event, msg.arg || msg.args || ""); return; }
      if (!msg?.data || !msg?.arg) return;

      const chan = msg.arg.channel;
      const action = msg.action;
      const payload = Array.isArray(msg.data) ? msg.data[0] : msg.data;

      if (chan === "ticker") {
        const lastPr = payload?.lastPr;
        const bidPr  = payload?.bidPr;
        const askPr  = payload?.askPr;
        const quoteV = payload?.quoteVolume;
        const fr = payload?.fundingRate;

        setTickerData((prev) => ({
          ...(prev || {}),
          symbol: msg.arg?.instId,
          lastPrice: lastPr != null ? String(lastPr) : prev?.lastPrice,
          volume24: quoteV != null ? String(quoteV) : (prev?.volume24 ?? ""),
          bid: bidPr, ask: askPr,
        }));
        if (fr != null) setFundingRate(Number(fr) || 0);
        return;
      }

      if (chan === "books") {
        const seq = payload?.seq ?? null;
        const asks = toPairs(payload?.asks || []);
        const bids = toPairs(payload?.bids || []);

        if (action === "snapshot" || !hasSnapshotRef.current) {
          orderBookRef.current = {
            asks: asks.sort((a,b)=>+a[0]-+b[0]).slice(0,20),
            bids: bids.sort((a,b)=>+b[0]-+a[0]).slice(0,20),
          };
          setOrderBook(scaleBook(orderBookRef.current)); // 👈 масштабируем
          hasSnapshotRef.current = true;
          lastSeqRef.current = seq;
          log("📊 snapshot set. seq=", seq);
          return;
        }

        if (seq != null && lastSeqRef.current != null && Number(seq) <= Number(lastSeqRef.current)) {
          log("⏭ stale update skipped seq=", seq, "local=", lastSeqRef.current);
          return;
        }
        lastSeqRef.current = seq;

        if (asks.length) applyDeltas(asks, "asks");
        if (bids.length) applyDeltas(bids, "bids");
      }
    } catch (e) {
      log("❌ parse error:", e);
    }
  }, [applyDeltas, scaleBook]);

  useEffect(() => {
    if (!symbol) return;

    const instId = symbol.includes("USDT") ? symbol : `${symbol}USDT`;

    (async () => {
      // получаем sizeMultiplier ДО WS
      contractSizeRef.current = await getFuturesContractSize("bitget", instId);
    })();

    setIsConnected(false);
    setError(null);
    setTickerData(null);
    setFundingRate(0);
    setOrderBook({ asks: [], bids: [] });

    orderBookRef.current = { asks: [], bids: [] };
    hasSnapshotRef.current = false;
    lastSeqRef.current = null;

    const ws = new WebSocket("wss://ws.bitget.com/v2/ws/public");
    wsRef.current = ws;

    ws.onopen = () => {
      log("🔗 open:", instId);
      setIsConnected(true);
      subscribeAll(ws, instId);

      pingTimerRef.current = setInterval(() => {
        try { ws.send("ping"); } catch {}
      }, 30000);
    };

    ws.onmessage = handleMessage;

    ws.onerror = (e) => {
      log("❌ ws error:", e);
      setError(e?.message || "WS error");
      setIsConnected(false);
    };

    ws.onclose = () => {
      log("🔌 close");
      setIsConnected(false);
    };

    return () => {
      try { unsubscribeAll(ws, instId); ws.close(); } catch {}
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
      wsRef.current = null;
    };
  }, [symbol, subscribeAll, unsubscribeAll, handleMessage]);

  return { isConnected, error, orderBook, tickerData, fundingRate };
};
